import React, { PropsWithChildren } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import { Dialog as DialogStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import Modal, { ModalProps } from '../Modal/Modal'
import IconButton from '../IconButton'
import { CommonProps } from '../helperTypes'

const CloseIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='#063B9E' width='18px' height='18px'>
    <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
    <path d='M0 0h24v24H0z' fill='none' />
  </svg>
)

interface Props extends ModalProps {
  /** An optional title for the Dialog.
   * If provided instead of a DialogTitle children,
   * a close cross button will be shown. */
  title?: string
  /** Determines the container for scrolling the dialog. */
  scroll?: 'body' | 'dialog'
}

type DeprecatedProps = {
  disablePortal?: boolean
  fullScreen?: boolean
}

export type DialogProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(DialogStyles, 'dialog')

const Dialog = (props: DialogProps) => {
  useDeprecatedProps('Dialog', { disablePortal: undefined }, props)
  const classes = useStyles()

  const handleBackdropClick: React.ReactEventHandler = (event) => {
    if (event.target !== event.currentTarget) {
      return
    }
    if (onBackdropClick) {
      onBackdropClick()
    } else if (!disableBackdropClick && onClose) {
      onClose()
    }
  }

  const {
    children,
    className,
    disableBackdrop,
    disableBackdropClick,
    disableEscapeKeyDown,
    onBackdropClick,
    onClose,
    open,
    scroll = 'dialog',
    style,
    title,
    ...rest
  } = props

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableBackdrop={disableBackdrop}
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableEscapeKeyDown}
      closeAfterDelay={0.1}
      {...rest}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className={clsx(
              classes.wrapper,
              scroll === 'dialog' ? classes.wrapper_scrollDialog : classes.wrapper_scrollBody,
            )}
            role='none presentation'
            tabIndex={-1}
            onMouseDown={handleBackdropClick}
          >
            <section
              className={clsx(
                className,
                classes.root,
                scroll === 'dialog' ? classes._scrollDialog : classes._scrollBody,
              )}
              style={style}
              role='dialog'
              aria-modal='true'
            >
              {title && <DialogHeader>{title}</DialogHeader>}
              <IconButton className={classes.close} aria-label='Close' variant='ghost' onClick={onClose}>
                <CloseIcon />
              </IconButton>
              {children}
            </section>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  )
}

export default Dialog

type DialogChildProps = PropsWithChildren<CommonProps>

export const DialogContent = (props: DialogChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.content)} {...rest}>
      {children}
    </div>
  )
}

export const DialogHeader = (props: DialogChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <header className={clsx(className, classes.header)} {...rest}>
      {children}
    </header>
  )
}

export const DialogTitle = ({ children }: DialogChildProps) => {
  useDeprecatedComponent('DialogTitle', 'Please use DialogHeader solely.')
  return <span>{children}</span>
}

export const DialogActions = (props: DialogChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.actions)} {...rest}>
      {children}
    </div>
  )
}
